import { SLoader } from "@avalara/skylab-sdk/react";
import throttle from "lodash/throttle";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { CrossTabCoordinator } from "../../app/session/CrossTabCoordinator";
import { forceRefreshSessionAsync } from "../../app/sessionSlice";
import store from "../../app/store";
import axiosObject from "../../axios";
import getConfig from "../../config";
import toast from "../../hooks/toast";
import { ACTIVITY_STORAGE_KEY, UnhandledError } from "../../shared/constants";
import { logger, setAuthenticationInProgress } from "../../shared/Logger";
import { errorResponse } from "../../shared/responseUtils";
import { cleanupSessionRefreshTimeout } from "../../shared/sessionTimeoutCleanup";
import { redirectToLoginPage } from "../../shared/sessionUtility";
import { isOneWebDomainRuntime } from "../../shared/Utils";
import { DebugLogger } from "../../utils/DebugLogger";
import { UnAuthorizedResponse } from "./constant";
import { getMessage } from "./errorBoundaryUtils";
import ErrorPage from "./ErrorPage";

// Config is retrieved lazily inside functions to support runtime configuration
const getLazyConfig = () => {
    const config = getConfig();
    return {
        appEnv: config.appEnv,
        ecmApiHost: config.hostnames.ecmApiHost,
        dataService: config.hostnames.dataService,
    };
};

// Constants
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const THROTTLE_WAIT = 1000; // 1 second
const NETWORK_ERROR_CODES = ["ECONNABORTED", "ERR_NETWORK"];
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"];

// Component State Enum
const COMPONENT_STATES = {
    NORMAL: "normal",
    AUTHENTICATING: "authenticating",
};

const logErrorInfo = async event => {
    const { message, filename, lineno, colno, error } = event;
    const traceId = error?.response?.headers["x-trace-id"] ?? "unknown";

    const params = {
        message: message || error?.message || "Unknown error",
        stack: error?.stack || "", // This gives you the full JS stack trace
        filename: filename || "unknown",
        lineno: lineno || "-",
        colno: colno || "-",
        traceId,
    };

    await logger(2, 4, params);
};

window.addEventListener("error", logErrorInfo);
window.addEventListener("unhandledrejection", event => {
    // Handle authentication failures silently to avoid console spam
    if (event.reason?.isAuthenticationFailure && event.reason?.shouldSilentlyFail) {
        event.preventDefault();
        return;
    }
    // Log other unhandled rejections normally - handle the promise to prevent recursion
    logErrorInfo(event).catch(error => {
        if (getLazyConfig().appEnv !== "prd" && getLazyConfig().appEnv !== "sbx") {
            // eslint-disable-next-line no-console
            console.error(
                "Failed to log unhandled rejection:",
                error.message || error?.reason?.message
            );
        }
    });
});

function ErrorBoundaryWrapper(props, ref) {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    const errorBoundaryRef = useRef();

    useImperativeHandle(ref, () => ({
        removeError: errorBoundaryRef.current?.removeError,
    }));

    return (
        <ErrorBoundary
            ref={errorBoundaryRef}
            {...props}
            searchParams={searchParams}
            dispatch={dispatch}
        />
    );
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            lastActivityTime: parseInt(localStorage.getItem(ACTIVITY_STORAGE_KEY) || "0", 10),
            status: 0,
            errorType: null,
            isAuthenticating: false,
        };
        this.abortController = new AbortController();
        this.requestQueue = [];
        this.activeTimeouts = new Set();
        this.authenticationTimeout = null;
        this.isRedirecting = false;
        this.componentState = COMPONENT_STATES.NORMAL;

        this.updateActivity = throttle(this.handleActivity, THROTTLE_WAIT, {
            leading: true,
            trailing: true,
        });
        this.requestInterceptor = null;
        this.responseInterceptor = null;

        this.errorHandler = async error => {
            let params = {};
            if (error?.request) {
                params = {
                    method: error?.config?.method,
                    url: error?.config?.url,
                    status: error?.request?.status,
                    statusText: error?.request?.statusText,
                };
            } else if (error?.response) {
                params = {
                    method: error?.config?.method,
                    url: error?.config?.url,
                    status: error?.response?.status,
                    statusText: error?.response?.statusText,
                };
            }
            await logger(1, 4, params);
            // eslint-disable-next-line
            console.error("API Error in ErrorBoundary: ", params);
        };
        this.removeError = this.removeError.bind(this);
    }

    // State management helper methods
    isAuthenticating() {
        return this.componentState === COMPONENT_STATES.AUTHENTICATING;
    }

    isNormal() {
        return this.componentState === COMPONENT_STATES.NORMAL;
    }

    // For backward compatibility with request queueing logic
    get sessionRefreshInProgress() {
        return this.isAuthenticating();
    }

    setAuthenticating() {
        this.componentState = COMPONENT_STATES.AUTHENTICATING;
        this.setState({ isAuthenticating: true });
        // Notify logger to pause logging during authentication
        setAuthenticationInProgress(true);
    }

    resetAuthenticationState(skipRedirectReset = false) {
        this.componentState = COMPONENT_STATES.NORMAL;
        // Conditionally clear redirect flag as part of authentication state reset
        if (!skipRedirectReset) {
            this.isRedirecting = false;
        }
        // Clear both authentication and error states together
        this.setState({ isAuthenticating: false, hasError: false, errorType: null });
        // Notify logger that authentication is complete
        setAuthenticationInProgress(false);
    }

    // Circuit breaker for session refresh - consolidated logic
    sessionRefreshCircuitBreaker = {
        attempts: 0,
        maxAttempts: 3,
        lastReset: Date.now(),
        resetInterval: 1 * 60 * 1000, // 1 minute

        canAttempt() {
            // Auto-reset if enough time has passed
            if (Date.now() - this.lastReset > this.resetInterval) {
                this.reset();
            }
            return this.attempts < this.maxAttempts;
        },

        increment() {
            this.attempts++;
            return this.attempts;
        },

        reset() {
            this.attempts = 0;
            this.lastReset = Date.now();
        },
    };

    // Handle authentication failure with immediate redirect
    handleAuthenticationFailure(reason = "Authentication failed") {
        // Clean up session refresh timeout to prevent memory leaks
        cleanupSessionRefreshTimeout();

        // Log the failure reason for debugging
        if (getLazyConfig().appEnv !== "prd" && getLazyConfig().appEnv !== "sbx") {
            // eslint-disable-next-line no-console
            console.warn("Authentication failure:", reason);
        }

        // Set redirect flag to prevent any error rendering
        this.isRedirecting = true;

        // Reset authentication state but preserve redirect flag to prevent race condition
        this.resetAuthenticationState(true); // skipRedirectReset = true

        // Force immediate redirect using setTimeout to break out of current execution context
        const immediateRedirectTimeout = setTimeout(() => {
            if (isOneWebDomainRuntime()) {
                window.AvaApp.scriptErrorHandler();
            } else {
                redirectToLoginPage();
            }
        }, 0);

        // Track the immediate redirect timeout
        this.activeTimeouts.add(immediateRedirectTimeout);

        // Safety timeout: Reset flag if redirect somehow fails (5 seconds)
        const safetyTimeout = setTimeout(() => {
            if (this.isRedirecting) {
                this.isRedirecting = false;
                if (getLazyConfig().appEnv !== "prd" && getLazyConfig().appEnv !== "sbx") {
                    // eslint-disable-next-line no-console
                    console.warn("Redirect timeout - resetting isRedirecting flag");
                }
            }
            // Clean up the timeout from tracking AFTER it executes
            this.activeTimeouts.delete(safetyTimeout);
        }, 5000);

        // Track the safety timeout
        this.activeTimeouts.add(safetyTimeout);
    }

    static isNetworkError(error) {
        return (
            (error?.code && NETWORK_ERROR_CODES.some(code => error.code.includes(code))) ||
            error.message === "Network Error"
        );
    }

    static getDerivedStateFromError(error) {
        // Set status to 500 to show proper error page instead of generic glitch message
        return { hasError: true, error, errorType: "react", status: "500" };
    }

    static resetActivityTimer() {
        localStorage.setItem(ACTIVITY_STORAGE_KEY, Date.now().toString());
    }

    removeError() {
        // Clear authentication timeout if active
        if (this.authenticationTimeout) {
            clearTimeout(this.authenticationTimeout);
            this.authenticationTimeout = null;
        }

        // Reset circuit breaker on manual error removal
        this.sessionRefreshCircuitBreaker.reset();
        // Reset authentication state (now also clears error states and redirect flag)
        this.resetAuthenticationState();
    }

    // Add method to check if error should be shown
    shouldShowErrorPage(error) {
        // Don't show error page if component is unmounting or authenticating
        if (!this.isNormal()) {
            return false;
        }

        // Enhanced detection for aborted/cancelled requests
        if (this.isAbortedRequest(error)) {
            return false;
        }

        // Don't show error page for 401s (handled by redirect)
        if (error?.response?.status === 401) {
            return false;
        }

        // Don't show error page for network errors during inactivity
        return !(ErrorBoundary.isNetworkError(error) && !this.isUserActive());
    }

    // Simplified method to detect aborted requests - focuses on core patterns
    isAbortedRequest(error) {
        return (
            error?.code === "ERR_CANCELED" ||
            error?.name === "AbortError" ||
            (error?.config?.signal === this.abortController.signal &&
                this.abortController.signal.aborted) ||
            error?.message === "Component unmounted" ||
            error?.message === "Request queue timeout"
        );
    }

    async handleReactComponentError(error, info) {
        // Only log and show error page if it's a genuine component error
        if (this.shouldShowErrorPage(error)) {
            const params = {
                message: error.message,
                jsStack: error.stack, // from the JS engine
                componentStack: info?.componentStack, // from React
                filename: "ReactComponentError",
                lineno: "-",
                colno: "-",
            };

            await logger(2, 4, params);
            // eslint-disable-next-line
            console.error("React Component Error in ErrorBoundary: ", params.componentStack);
        }
    }

    componentDidMount() {
        this.setupUserActivityTracking();
        this.setupAxiosInterceptors();
    }

    componentDidCatch(error, info) {
        // Only set error state if we should show the error page
        if (this.shouldShowErrorPage(error)) {
            this.setState({ hasError: true, status: 0, errorType: "react" });
        }
        this.handleReactComponentError(error, info).catch(error => {
            if (getLazyConfig().appEnv !== "prd" && getLazyConfig().appEnv !== "sbx") {
                // eslint-disable-next-line no-console
                console.error("Failed to handle react component error:", error);
            }
        });
    }

    componentWillUnmount() {
        // Clean up session refresh timeout to prevent memory leaks
        cleanupSessionRefreshTimeout();

        this.cleanupUserActivityTracking();

        this.abortController.abort();

        // Clear authentication timeout
        if (this.authenticationTimeout) {
            clearTimeout(this.authenticationTimeout);
            this.authenticationTimeout = null;
        }

        // Reset redirect flag to prevent memory leaks and stuck states
        this.isRedirecting = false;

        // Clear all timeouts and queued requests to prevent memory leaks
        this.clearQueuedRequests("Component unmounted");

        // Clean up cross-tab coordination keys to prevent stale locks
        CrossTabCoordinator.cleanupCoordinationKeys();

        // Remove handlers, so Garbage Collector will get rid of if WrappedComponent will be removed
        this.cleanupAxiosInterceptors();
    }

    setupUserActivityTracking = () => {
        const { signal } = this.abortController;

        ACTIVITY_EVENTS.forEach(event => {
            document.addEventListener(event, this.updateActivity, { signal });
        });

        window.addEventListener("storage", this.handleStorageChange, { signal });

        if (!this.state.lastActivityTime) {
            this.handleActivity();
        }
    };

    cleanupUserActivityTracking = () => {
        this.updateActivity.cancel();
    };

    handleStorageChange = event => {
        if (event.key === ACTIVITY_STORAGE_KEY) {
            const newActivityTime = parseInt(event.newValue || "0", 10);
            this.setState({ lastActivityTime: newActivityTime });
        }
    };

    isUserActive = () => {
        return Date.now() - this.state.lastActivityTime <= INACTIVITY_TIMEOUT;
    };

    queueRequestForRetry = request => {
        // Add request to queue and return a promise that resolves when session refresh completes
        return new Promise((resolve, reject) => {
            if (!this.sessionRefreshInProgress) {
                reject(new Error("Session refresh not in progress"));
                return;
            }

            if (!this.requestQueue) {
                this.requestQueue = [];
            }

            // Prevent unbounded queue growth (max 50 requests)
            if (this.requestQueue.length >= 50) {
                reject(new Error("Request queue is full"));
                return;
            }

            const timeoutId = setTimeout(() => {
                this.activeTimeouts.delete(timeoutId);
                reject(new Error("Request queue timeout"));
            }, 10000); // 10 second timeout for queued requests

            // Track the timeout ID
            this.activeTimeouts.add(timeoutId);

            this.requestQueue.push({
                request,
                resolve: response => {
                    this.clearTimeout(timeoutId);
                    resolve(response);
                },
                reject: error => {
                    this.clearTimeout(timeoutId);
                    reject(error);
                },
            });
        });
    };

    retryQueuedRequests = async () => {
        if (!this.requestQueue || this.requestQueue.length === 0) {
            return;
        }

        const requests = [...this.requestQueue];
        this.requestQueue = [];

        // Process requests with timeout protection and AbortController
        const promises = requests.map(({ request, resolve, reject }) => {
            const timeoutPromise = new Promise((_, timeoutReject) => {
                setTimeout(() => {
                    timeoutReject(new Error("Request timeout"));
                }, 10000); // 10 second timeout
            });

            // Add AbortController to the request
            const requestWithAbort = {
                ...request,
                signal: this.abortController.signal,
            };

            const requestPromise = axiosObject(requestWithAbort).then(resolve).catch(reject);

            return Promise.race([requestPromise, timeoutPromise]);
        });

        try {
            await Promise.allSettled(promises);
        } catch (error) {
            // eslint-disable-next-line
            console.error("Error processing queued requests:", error);
        }
    };

    clearQueuedRequests = reason => {
        // Clear all active timeouts first
        this.activeTimeouts.forEach(timeoutId => {
            clearTimeout(timeoutId);
        });
        this.activeTimeouts.clear();

        // Reject all queued requests with silent error handling
        if (this.requestQueue && this.requestQueue.length > 0) {
            this.requestQueue.forEach(({ reject }) => {
                // Create a custom error that can be identified and handled silently
                const error = new Error(reason);
                error.isAuthenticationFailure = true;
                error.shouldSilentlyFail = true;
                reject(error);
            });
            this.requestQueue = [];
        }
    };

    rejectQueuedRequests = reason => {
        // Silently reject queued requests when authentication fails
        // This prevents console spam from uncaught promise rejections
        this.clearQueuedRequests(reason);
    };

    clearTimeout = timeoutId => {
        clearTimeout(timeoutId);
        this.activeTimeouts.delete(timeoutId);
    };

    handleActivity = () => {
        const currentTime = Date.now();
        localStorage.setItem(ACTIVITY_STORAGE_KEY, currentTime.toString());
        this.setState({ lastActivityTime: currentTime });
    };

    // Request Interceptor Setup
    setupRequestInterceptor = () => {
        // Set axios interceptors
        const api = `/api/`;
        this.requestInterceptor = axiosObject.interceptors.request.use(
            req => {
                if (req.url.startsWith(getLazyConfig().dataService)) {
                    return req;
                }
                const { searchParams } = this.props;
                let avaTaxCompanyId;

                if (searchParams) {
                    avaTaxCompanyId = searchParams.get("companyId");
                }
                // Don't reset error state here - let responses handle it
                const { session } = store.getState();
                if (req.url.includes("session")) {
                    return req;
                }

                const urlSplit = req.url.split(api);
                const version = urlSplit[1].split("/")[0];
                const methodName = urlSplit[1].replace(version, "");

                const companiesPattern = /\/companies\/\d+/;
                if (companiesPattern.test(methodName)) {
                    req.url = `${urlSplit[0]}${api}${version}${methodName}`;
                } else {
                    req.url = `${urlSplit[0]}${api}${version}/companies/${
                        avaTaxCompanyId ?? session.activeCompany.id
                    }${methodName}`;
                }

                return req;
            },
            async error => {
                // Only set error state if this is a genuine request setup error
                if (this.shouldShowErrorPage(error)) {
                    this.setState({ hasError: true, status: 0, errorType: "request" });
                }
                await this.errorHandler(error);
                return Promise.reject(error);
            }
        );
    };

    // Response Interceptor Setup
    setupResponseInterceptor = () => {
        this.responseInterceptor = axiosObject.interceptors.response.use(
            async res => {
                // Clear error state on successful responses
                if (this.state.hasError && this.state.errorType !== "react") {
                    this.setState({ hasError: false, errorType: null });
                }

                // Reset redirect flag on successful API responses (recovery)
                if (this.isRedirecting) {
                    this.isRedirecting = false;
                }

                return res;
            },
            async error => {
                const originalRequest = error.config;
                const traceId = error?.response?.headers["x-trace-id"] ?? null;
                if (
                    error?.config?.method === "get" &&
                    error?.response?.request?.status === 500 &&
                    error?.response?.request?.responseURL.includes("certificate-validation")
                ) {
                    const [showToast] = toast();
                    showToast(
                        "error",
                        "Problem with form reader auto-validation service. Please try again.",
                        "auto-validation-error"
                    );
                    return Promise.reject(error);
                }

                const parseError = errorResponse(error);

                if (error.response && error.response.status && error.response.status === 401) {
                    // AvaApp: block the ECM-timer-based 401 path and retry once.
                    // The request interceptor (attachAvaAppTokenToRequest) already
                    // attaches a fresh shell token to every outgoing request, so there
                    // is no need to fetch one here — it will be re-attached when the
                    // retry goes through axiosObject.
                    if (isOneWebDomainRuntime()) {
                        if (originalRequest._avaAppRetried) {
                            this.handleAuthenticationFailure("AvaApp token refresh failed");
                            return Promise.reject(error);
                        }
                        originalRequest._avaAppRetried = true;
                        return axiosObject(originalRequest);
                    }

                    // If session refresh is already in progress, queue this request
                    if (this.sessionRefreshInProgress) {
                        return this.queueRequestForRetry(originalRequest);
                    }

                    // Check circuit breaker before attempting session refresh
                    if (!this.sessionRefreshCircuitBreaker.canAttempt()) {
                        // Circuit breaker tripped - force logout without retry
                        this.rejectQueuedRequests(
                            `Session refresh circuit breaker tripped (${this.sessionRefreshCircuitBreaker.attempts}/${this.sessionRefreshCircuitBreaker.maxAttempts} attempts)`
                        );

                        // Handle circuit breaker tripped scenario
                        this.handleAuthenticationFailure("Circuit breaker tripped");
                        return Promise.reject(error);
                    }

                    // Set authentication state for the first 401 request
                    this.setAuthenticating();
                    this.sessionRefreshCircuitBreaker.increment();

                    // Clear any existing timeout before setting new one
                    if (this.authenticationTimeout) {
                        clearTimeout(this.authenticationTimeout);
                    }

                    // Set a timeout to prevent infinite loading state (30 seconds)
                    this.authenticationTimeout = setTimeout(() => {
                        if (this.isAuthenticating()) {
                            // Handle authentication timeout
                            this.handleAuthenticationFailure("Authentication timeout (30 seconds)");
                        }
                    }, 30000);

                    try {
                        DebugLogger.log(
                            "ErrorBoundary starting coordinated refresh for 401 error",
                            undefined,
                            "CrossTab"
                        );

                        // Use the coordinated session refresh instead of direct API call
                        // This prevents duplicate refreshes across tabs and ensures proper coordination
                        // Use false for user-initiated refresh, true to bypass minimum interval for error recovery
                        await this.props.dispatch(forceRefreshSessionAsync(false, true));

                        // Get the updated session state after coordinated refresh
                        const currentState = store.getState();

                        // Check if the coordinated refresh was successful
                        if (currentState.session.isAuthenticated) {
                            DebugLogger.log(
                                "ErrorBoundary coordinated refresh successful, retrying queued requests",
                                undefined,
                                "CrossTab"
                            );

                            try {
                                // Session is already initialized by forceRefreshSessionAsync, no need to call initializeSession again

                                // Retry all queued requests (other requests that came in during refresh)
                                await this.retryQueuedRequests();

                                // Clear authentication timeout and reset state
                                if (this.authenticationTimeout) {
                                    clearTimeout(this.authenticationTimeout);
                                    this.authenticationTimeout = null;
                                }

                                // Reset circuit breaker on successful authentication
                                this.sessionRefreshCircuitBreaker.reset();
                                // Reset authentication state (now also clears redirect flag)
                                this.resetAuthenticationState();

                                // Retry the original request that triggered the 401
                                return axiosObject(originalRequest);
                            } catch (dispatchError) {
                                DebugLogger.log(
                                    "ErrorBoundary failed to retry requests",
                                    dispatchError,
                                    "CrossTab"
                                );
                                this.rejectQueuedRequests("Session initialization failed");

                                // Handle session initialization failure
                                this.handleAuthenticationFailure("Session initialization failed");
                            }
                        } else {
                            DebugLogger.log(
                                "ErrorBoundary coordinated refresh failed - not authenticated",
                                undefined,
                                "CrossTab"
                            );

                            this.rejectQueuedRequests("Session refresh failed - not authenticated");

                            // Handle session not authenticated
                            this.handleAuthenticationFailure(
                                "Session refresh failed - not authenticated"
                            );
                        }
                    } catch (sessionError) {
                        DebugLogger.log(
                            "ErrorBoundary coordinated refresh threw exception",
                            sessionError,
                            "CrossTab"
                        );

                        this.rejectQueuedRequests("Session refresh failed");

                        // Handle session refresh exception
                        this.handleAuthenticationFailure("Session refresh failed");
                    }
                } else if (parseError && parseError?.code) {
                    const responseObj = getMessage(error);
                    if (responseObj?.responseMessage) {
                        if (
                            getLazyConfig().appEnv !== "prd" &&
                            getLazyConfig().appEnv !== "sbx" &&
                            responseObj?.responseMessage?.messageType === UnhandledError
                        ) {
                            // Only show error page if this is a genuine unhandled error
                            if (this.shouldShowErrorPage(error)) {
                                this.setState({
                                    hasError: true,
                                    status: parseError?.status.toString(),
                                    traceId,
                                    errorType: "api",
                                });
                            }
                            await this.errorHandler(error);
                        }
                        return Promise.reject(responseObj?.responseMessage);
                    }
                    if (responseObj) {
                        return Promise.reject(responseObj);
                    }
                    if (parseError?.status === 500) {
                        // Only show error page for genuine 500 errors
                        if (this.shouldShowErrorPage(error)) {
                            this.setState({
                                hasError: true,
                                status: parseError?.status.toString(),
                                traceId,
                                errorType: "api",
                            });
                        }
                        await this.errorHandler(error);
                    }
                } else if (ErrorBoundary.isNetworkError(error) && !this.isUserActive()) {
                    // Handle network errors during inactivity - redirect without error page
                    if (isOneWebDomainRuntime()) {
                        window.AvaApp.scriptErrorHandler();
                    } else {
                        this.redirectToLogout();
                    }
                } else {
                    // Handle other errors
                    if (error?.config?.url.startsWith(getLazyConfig().dataService)) {
                        return;
                    }

                    if (
                        error?.response?.status === UnAuthorizedResponse.code &&
                        error?.response?.data?.Message === UnAuthorizedResponse.Message
                    ) {
                        // eslint-disable-next-line
                        console.error("You dont have access to ", error?.config?.url);
                        return Promise.reject(error);
                    }

                    // Only show error page if this should be shown
                    if (this.shouldShowErrorPage(error)) {
                        // Skip showing error page for Network Errors (no response status) as they are often transient
                        // and successful responses will clear them anyway
                        const hasValidStatus = error?.response?.status !== undefined;
                        if (hasValidStatus) {
                            this.setState({
                                hasError: true,
                                status: error?.response?.status?.toString(),
                                traceId,
                                errorType: "api",
                            });
                        }
                        // Always call errorHandler for logging purposes
                        await this.errorHandler(error);
                    }
                }
                return Promise.reject(error);
            }
        );
    };

    // Setup and Cleanup AxiosInterceptors

    setupAxiosInterceptors = () => {
        this.setupRequestInterceptor();
        this.setupResponseInterceptor();
    };

    cleanupAxiosInterceptors = () => {
        if (this.requestInterceptor !== null) {
            axiosObject.interceptors.request.eject(this.requestInterceptor);
        }
        if (this.responseInterceptor !== null) {
            axiosObject.interceptors.response.eject(this.responseInterceptor);
        }
    };

    redirectToLogout = () => {
        try {
            // Clean up session refresh timeout to prevent memory leaks
            cleanupSessionRefreshTimeout();

            // Only remove the activity storage key
            localStorage.removeItem(ACTIVITY_STORAGE_KEY);

            // Construct base URL
            const { appEnv, ecmApiHost } = getLazyConfig();
            const logoutUrl = new URL(
                "/api/v3/auth/logout",
                `${appEnv === "local" ? "http" : "https"}://${ecmApiHost}`
            );

            // Don't set error state when redirecting
            window.location.href = logoutUrl.toString();
        } catch (error) {
            // Even if redirect fails, try to remove activity storage key
            try {
                localStorage.removeItem(ACTIVITY_STORAGE_KEY);
            } catch (storageError) {
                // eslint-disable-next-line
                console.error("Failed to remove activity storage key:", storageError);
            }
            this.errorHandler(error).catch(error => {
                if (getLazyConfig().appEnv !== "prd" && getLazyConfig().appEnv !== "sbx") {
                    // eslint-disable-next-line no-console
                    console.log("Failed to handle error:", error);
                }
            });
            // Use redirectToLoginPage instead of hard reload for better UX
            if (isOneWebDomainRuntime()) {
                window.AvaApp.scriptErrorHandler();
            } else {
                redirectToLoginPage();
            }
        }
    };

    // Loading component for authentication state
    renderAuthenticationLoading() {
        return (
            <div className="flex dl-flex-fill-height dl-flex-center">
                <h1>Loading ...</h1>
                <SLoader id="authentication-loader" className="large" aria-live="polite" loading />
            </div>
        );
    }

    render() {
        // Never show anything if we're redirecting
        if (this.isRedirecting) {
            return this.renderAuthenticationLoading();
        }

        // Prioritize authentication loading over error page to prevent flash
        if (this?.state?.isAuthenticating) {
            return this.renderAuthenticationLoading();
        }

        // Don't show error page if we're in the middle of authentication flow
        // (prevents flash when hasError is briefly true during redirect)
        if (this?.state?.hasError && this.componentState === COMPONENT_STATES.NORMAL) {
            return <ErrorPage type={this?.state?.status} traceId={this?.state?.traceId} />;
        }

        return this?.props?.children;
    }
}

export default forwardRef(ErrorBoundaryWrapper);
